import type { PatchJob, PatchTask, PatchVerification } from '../../api/client'
import { useAuth } from '../../auth/AuthContext'
import { formatDateTime } from '../common'
import { t } from '../../i18n/lingua'

/**
 * The words of the patching console, in one place: every status, kind and
 * reason the Control Plane sends as a code becomes a sentence an operator
 * reads without a manual. Status chips always carry the word, never the colour
 * alone.
 */

/** Approving, canceling and rolling back touch machines: tenant administrators only. */
export function useIsTenantAdmin(): boolean {
  const { principal } = useAuth()
  const roles = principal?.roles ?? []
  return roles.includes('tenant_admin') || roles.includes('provider_admin')
}

export type Tone = 'ok' | 'warn' | 'error' | 'info' | 'muted'

export function chipClass(tone: Tone): string {
  return `chip patch-chip patch-chip--${tone}`
}

export function severityLabel(s: string): string {
  switch (s) {
    case 'critica': return t('Critica')
    case 'alta': return t('Alta')
    case 'media': return t('Media')
    case 'bassa': return t('Bassa')
    default: return s || '—'
  }
}

export function severityTone(s: string): Tone {
  if (s === 'critica') return 'error'
  if (s === 'alta') return 'warn'
  if (s === 'media') return 'info'
  return 'muted'
}

export function SeverityChip({ severity }: { severity: string }) {
  if (!severity) return null
  return <span className={chipClass(severityTone(severity))}>{severityLabel(severity)}</span>
}

export function priorityLabel(p: string): string {
  switch (p) {
    case 'urgent': return t('Urgente')
    case 'high': return t('Alta')
    case 'medium': return t('Media')
    case 'low': return t('Bassa')
    default: return p
  }
}

export function PriorityChip({ priority }: { priority: string }) {
  const tone: Tone = priority === 'urgent' ? 'error' : priority === 'high' ? 'warn'
    : priority === 'medium' ? 'info' : 'muted'
  return <span className={chipClass(tone)}>{priorityLabel(priority)}</span>
}

export function KevChip() {
  return (
    <span className={chipClass('error')}
      title={t('Sfruttata attivamente: è nel catalogo CISA KEV delle vulnerabilità usate negli attacchi.')}>
      {t('Sfruttata')}
    </span>
  )
}

/** What a job or a task installs. */
export function kindLabel(k: string): string {
  switch (k) {
    case 'security_all': return t('Aggiornamenti di sicurezza')
    case 'all_updates': return t('Tutti gli aggiornamenti')
    case 'selected': return t('Patch scelte')
    case 'packages': return t('Pacchetti scelti')
    case 'kb': return t('KB scelte')
    case 'reboot': return t('Riavvio')
    case 'rollback': return t('Rollback')
    case 'software': return t('Programmi scelti')
    case 'software_all':
    case 'third_party': return t('Programmi di terze parti')
    default: return k
  }
}

export function patchKindLabel(k: string): string {
  switch (k) {
    case 'kb': return t('KB Windows')
    case 'package': return t('Pacchetto Linux')
    case 'software': return t('Software installato')
    default: return k
  }
}

export function platformLabel(p: string): string {
  if (p === 'windows') return 'Windows'
  if (p === 'linux') return 'Linux'
  return p || '—'
}

/** Why a machine is not ready to be patched. */
export function reasonLabel(r: string, missingTags: string[] = []): string {
  switch (r) {
    case 'no_agent': return t('Senza agente CENSO')
    case 'os_unknown': return t('Sistema operativo non riconosciuto')
    case 'missing_tags':
      return missingTags.length
        ? t('Manca il tag {k}', { k: missingTags.map((k) => `«${k}»`).join(', ') })
        : t('Manca il tag obbligatorio')
    case 'agent_offline': return t('Agente non in linea')
    case 'agent_update': return t('Agente da aggiornare')
    case 'busy': return t('Installazione in corso')
    case 'not_missing': return t('Nessuna delle patch scelte manca qui')
    case 'no_software_manager': return t('Né winget né Chocolatey')
    case 'linux_third_party': return t('Linux: con gli aggiornamenti del sistema')
    default: return r
  }
}

/** What the operator does about a reason. */
export function reasonHint(r: string): string {
  switch (r) {
    case 'no_agent': return t('Installa l’agente dalla scheda della macchina: il patching passa da lì.')
    case 'os_unknown': return t('Serve un inventario dall’interno (agente o WinRM/SSH) che dica Windows o Linux.')
    case 'missing_tags': return t('Imposta il tag dalla scheda della macchina: l’approvazione lo pretende.')
    case 'agent_offline': return t('L’intervento parte appena l’agente torna in linea.')
    case 'agent_update': return t('L’agente si aggiorna da solo; poi l’intervento parte.')
    case 'busy': return t('Una installazione alla volta: questa parte dopo.')
    case 'not_missing': return t('Non riceve niente da questo intervento.')
    case 'no_software_manager': return t('Si aggiornano solo Chrome ed Edge, con il loro aggiornatore: per gli altri programmi serve App Installer (winget) o Chocolatey sulla macchina.')
    case 'linux_third_party': return t('Su Linux i programmi vengono dai repository: si aggiornano con «Tutti gli aggiornamenti» o scegliendo il pacchetto.')
    default: return ''
  }
}

export function reasonTone(r: string): Tone {
  return ['no_agent', 'os_unknown', 'missing_tags'].includes(r) ? 'error' : 'warn'
}

export function ReadinessChips({ reasons, missingTags }: { reasons: string[]; missingTags?: string[] }) {
  if (!reasons.length) return <span className={chipClass('ok')}>{t('Pronta')}</span>
  return (
    <>
      {reasons.map((r) => (
        <span key={r} className={chipClass(reasonTone(r))} title={reasonHint(r)}>
          {reasonLabel(r, missingTags)}
        </span>
      ))}
    </>
  )
}

/** The status of one task on one machine, in words. */
export function taskStatus(task: PatchTask): { label: string; tone: Tone; hint: string } {
  const v = task.verification
  switch (task.status) {
    case 'proposed':
      if (task.message) return { label: t('Bloccata'), tone: 'error', hint: task.message }
      return { label: t('Da approvare'), tone: 'info', hint: t('Nessuna installazione prima dell’approvazione.') }
    case 'approved':
      return {
        label: t('In coda'), tone: 'info',
        hint: task.agent_online === false
          ? t('Parte quando l’agente torna in linea.')
          : task.agent_update
            ? t('Parte dopo l’aggiornamento dell’agente.')
            : t('Parte al prossimo contatto dell’agente (circa un minuto).'),
      }
    case 'scheduled':
      return {
        label: t('Programmata'), tone: 'info',
        hint: task.scheduled_at ? t('Parte il {d}', { d: formatDateTime(task.scheduled_at) })
          : t('Parte all’apertura della finestra di manutenzione.'),
      }
    case 'running': return { label: t('In installazione'), tone: 'info', hint: t('L’agente sta installando.') }
    case 'applied':
      if (v?.status === 'still_missing') {
        return { label: t('Installata, ancora mancante'), tone: 'warn', hint: verificationLabel(v) }
      }
      return { label: t('Installata'), tone: 'ok', hint: v ? verificationLabel(v) : '' }
    case 'needs_reboot':
      return {
        label: t('Da riavviare'), tone: 'warn',
        hint: task.allow_reboot
          ? t('Il riavvio è programmato: si completa quando la macchina riparte.')
          : t('Installata: si completa al riavvio, che non è stato concesso.'),
      }
    case 'failed': return { label: t('Non riuscita'), tone: 'error', hint: task.message || String(task.result?.summary ?? '') }
    case 'rolled_back': return { label: t('Rollback eseguito'), tone: 'muted', hint: '' }
    case 'canceled': return { label: t('Annullata'), tone: 'muted', hint: task.message || '' }
    default: return { label: task.status, tone: 'muted', hint: '' }
  }
}

export function TaskStatusChip({ task }: { task: PatchTask }) {
  const s = taskStatus(task)
  return <span className={chipClass(s.tone)} title={s.hint}>{s.label}</span>
}

export function verificationLabel(v: PatchVerification): string {
  switch (v.status) {
    case 'waiting_inventory': return t('Verifica: in attesa dell’inventario dopo l’installazione')
    case 'to_assess': return t('Verifica in corso')
    case 'verified': return t('Verificata: le patch non risultano più mancanti')
    case 'still_missing':
      return t('Ancora mancanti dopo l’installazione: {p}', { p: (v.still_missing ?? []).join(', ') })
    case 'reboot_pending': return t('Si completa al riavvio')
    case 'no_inventory': return t('Nessun inventario arrivato dopo l’installazione: verifica non fatta')
    case 'unverified': return t('Non verificabile qui: manca il motore di valutazione')
    default: return v.status
  }
}

export function VerificationChip({ v }: { v: PatchVerification | null | undefined }) {
  if (!v) return null
  const tone: Tone = v.status === 'verified' ? 'ok'
    : v.status === 'still_missing' || v.status === 'no_inventory' ? 'warn' : 'info'
  const short = v.status === 'verified' ? t('Verificata')
    : v.status === 'still_missing' ? t('Ancora mancante')
      : v.status === 'reboot_pending' ? t('Al riavvio')
        : v.status === 'no_inventory' ? t('Non verificata') : t('Verifica in corso')
  return <span className={chipClass(tone)} title={verificationLabel(v)}>{short}</span>
}

export function jobPhaseLabel(p: PatchJob['phase']): string {
  switch (p) {
    case 'to_approve': return t('Da approvare')
    case 'scheduled': return t('Programmato')
    case 'running': return t('In corso')
    case 'blocked': return t('Bloccato')
    case 'done': return t('Completato')
    case 'done_reboot': return t('Completato, da riavviare')
    case 'done_errors': return t('Completato con errori')
    case 'canceled': return t('Annullato')
    default: return p
  }
}

export function JobPhaseChip({ phase }: { phase: PatchJob['phase'] }) {
  const tone: Tone = phase === 'done' ? 'ok' : phase === 'done_errors' || phase === 'blocked' ? 'error'
    : phase === 'done_reboot' ? 'warn' : phase === 'canceled' ? 'muted' : 'info'
  return <span className={chipClass(tone)}>{jobPhaseLabel(phase)}</span>
}

/** «When» of a job, in a sentence. */
export function jobWhen(job: PatchJob): string {
  if (job.schedule === 'window') return t('nella finestra «{w}»', { w: job.window_name ?? '—' })
  if (job.schedule === 'at' && job.scheduled_at) {
    return t('il {d}', { d: formatDateTime(job.scheduled_at) })
  }
  return t('subito dopo l’approvazione')
}

export function rebootPolicy(allow: boolean, delay: number): string {
  return allow
    ? t('riavvio se serve, con {n} minuti di preavviso', { n: delay })
    : t('nessun riavvio: lo segnala soltanto')
}

/** One reason of the risk score, as the console says it (patch_risk_service). */
export function riskFactorLabel(f: { factor: string; detail: string }): string {
  switch (f.factor) {
    case 'severity': return t('gravità {s}', { s: severityLabel(f.detail).toLowerCase() })
    case 'kev': return t('sfruttata ora (CISA KEV)')
    case 'epss': return t('probabilità di sfruttamento {d}', { d: f.detail })
    case 'criticality': return t('macchina di un servizio {c}', { c: criticitaLabel(f.detail) })
    case 'production': return t('in produzione')
    case 'exposed': return t('esposta a Internet')
    case 'overdue': return t('oltre lo SLA')
    default: return f.factor
  }
}

function criticitaLabel(c: string): string {
  switch (c) {
    case 'critical': return t('critico')
    case 'high': return t('alto')
    case 'medium': return t('medio')
    default: return c
  }
}

/** An environment of the reports: the value of the environment tag, «-» for the machines without one. */
export function environmentLabel(e: string): string {
  return e === '-' ? t('senza ambiente') : e
}
